import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Tracks bulk-download exports started in THIS SPA session that are still running, so the app
 * can refresh the header credit balance the moment one finishes — from any route, without a
 * full page reload.
 *
 * Why this is global (not inside the Bulk Download Detail page): a bulk download is processed
 * server-side AFTER the export request returns, so credits are only consumed when the job
 * completes. The detail page polls that status, but the moment the user navigates away its poll
 * stops and the header balance goes stale until the next full page load. This store keeps the
 * in-progress ids alive across navigation; `useBulkDownloadCreditSync` (mounted once in the app
 * shell) polls them and refreshes credits on completion.
 *
 * In-memory by design: the only scenario that needs it is in-app navigation. A full reload /
 * logout re-bootstraps the credit balance anyway (and logout hard-redirects), so persisting the
 * ids would only risk re-polling stale jobs for the next user.
 */
export const useBulkDownloadTrackerStore = defineStore('bulkDownloadTracker', () => {
  // Bulk-download ids awaiting completion. A plain array (not a Set) so Pinia tracks it reactively
  // and `useBulkDownloadCreditSync` can watch it to start/stop its poll loop.
  const pendingIds = ref<string[]>([])

  /** Start watching a just-started bulk download for completion. No-op on a blank/duplicate id. */
  function track(id: string): void {
    if (!id || pendingIds.value.includes(id)) return
    pendingIds.value = [...pendingIds.value, id]
  }

  /** Stop watching an id — once it has completed, failed, or is being handled elsewhere. */
  function untrack(id: string): void {
    if (!pendingIds.value.includes(id)) return
    pendingIds.value = pendingIds.value.filter((pending) => pending !== id)
  }

  function $reset(): void {
    pendingIds.value = []
  }

  return { pendingIds, track, untrack, $reset }
})
